import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  downloadMonthlyCsvExport,
  getApiUserErrorMessage,
} from "../shared/api/nest-api";
import { useAdminContext } from "./admin-context";

const defaultPeriod = new Date().toISOString().slice(0, 7);

export function MonthlyCsvExportPage() {
  const { stations } = useAdminContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(defaultPeriod);
  const [isDownloading, setIsDownloading] = useState(false);
  const toast = useToast();
  const stationParam = searchParams.get("station") ?? "";
  const selectedStation =
    stations.find((station) => station.slug === stationParam) ?? stations.at(0);
  const stationSlug = selectedStation?.slug ?? "";

  useEffect(
    function normalizeStationParam() {
      if (!stationSlug || stationParam === stationSlug) {
        return;
      }

      setSearchParams({ station: stationSlug }, { replace: true });
    },
    [setSearchParams, stationParam, stationSlug],
  );

  const handleDownload = async () => {
    if (!stationSlug) {
      return;
    }

    setIsDownloading(true);

    try {
      const { blob, fileName } = await downloadMonthlyCsvExport({
        period,
        stationSlug,
      });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);

      toast({
        title: "Monatsexport heruntergeladen",
        status: "success",
        duration: 5000,
      });
    } catch (error) {
      toast({
        title:
          getApiUserErrorMessage(error) ??
          "Der Monatsexport konnte nicht heruntergeladen werden.",
        status: "error",
        duration: 7000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Stack spacing="8">
      <Box>
        <Heading size="xl">Monatlicher CSV-Export</Heading>
        <Text color="gray.600" mt="2">
          Stationsbezogene Buchungs- und Verfügbarkeitsdaten als ZIP-Archiv.
        </Text>
      </Box>

      <Box
        bg="white"
        borderWidth="1px"
        borderRadius="lg"
        boxShadow="sm"
        p={{ base: "5", md: "8" }}
      >
        <Stack spacing="6" maxW="lg">
          <FormControl>
            <FormLabel>Monat</FormLabel>
            <Input
              type="month"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Station</FormLabel>
            <Select
              value={stationSlug}
              onChange={(event) =>
                setSearchParams({ station: event.target.value })
              }
              isDisabled={isDownloading || stations.length === 0}
            >
              {stations.length === 0 && (
                <option value="">Keine Station zugewiesen</option>
              )}
              {stations.map((station) => (
                <option key={station.id} value={station.slug}>
                  {station.name}
                </option>
              ))}
            </Select>
            <FormHelperText>
              {selectedStation ? `Slug: ${selectedStation.slug}` : " "}
            </FormHelperText>
          </FormControl>

          <HStack spacing="4" align="center" flexWrap="wrap">
            <Button
              colorScheme="blue"
              onClick={() => void handleDownload()}
              isDisabled={!stationSlug}
              isLoading={isDownloading}
              loadingText="Lädt..."
            >
              ZIP herunterladen
            </Button>
            <Text color="gray.500" fontSize="sm">
              Enthält mehrere CSV-Dateien
            </Text>
          </HStack>
        </Stack>
      </Box>
    </Stack>
  );
}
